// /src/constants/userMeta.ts
// Centralized user metadata for consistent labels/descriptions and Chip styling across UI.
// - Used by UserEdit/UserCreate (Autocomplete options, helper text, Chip display, etc.)
// - Keeps UI wording consistent and reduces duplicate mapping logic.

import { UserGroup, UserStatus } from '@models/user';
import type { UiChipStyle, UiMeta } from './uiMeta';

export type ChipStyle = UiChipStyle;

export type Meta = UiMeta & {
  description: string;
};

/**
 * User group UI metadata.
 * NOTE: PARTNER is included for type-safety and shared UI utilities,
 * even if some pages intentionally exclude it from selectors/filters.
 */
export const USER_GROUP_META = {
  [UserGroup.ADMINISTRATOR]: {
    label: 'Admin',
    description: 'Full access to all admin features',
    chip: { color: 'primary', variant: 'filled' },
  },
  [UserGroup.PRODUCT_MANAGER]: {
    label: 'Product',
    description: 'Product and stock management access',
    chip: { color: 'success', variant: 'filled' },
  },
  [UserGroup.MANAGER]: {
    label: 'Ops',
    description: 'Service operation access',
    chip: { color: 'default', variant: 'filled' },
  },
  [UserGroup.PARTNER]: {
    label: 'Partner',
    description: 'Partner account',
    chip: { color: 'warning', variant: 'outlined' },
  },
} as const satisfies Record<UserGroup, Meta>;

/**
 * User status UI metadata.
 */
export const USER_STATUS_META = {
  [UserStatus.ACTIVE]: {
    label: 'Active',
    description: 'User can sign in',
    chip: { color: 'success', variant: 'filled' },
  },
  [UserStatus.INACTIVE]: {
    label: 'Inactive',
    description: 'User cannot sign in',
    chip: { color: 'default', variant: 'outlined' },
  },
} as const satisfies Record<UserStatus, Meta>;

/* ------------------------------------------------------------------ */
/* Optional helpers (keep usage consistent, avoid undefined access)     */
/* ------------------------------------------------------------------ */

/** Safe label accessor (fallback to enum string). */
export const getUserGroupLabel = (g: UserGroup) => USER_GROUP_META[g]?.label ?? String(g);
/** Safe description accessor (fallback to empty). */
export const getUserGroupDescription = (g: UserGroup) => USER_GROUP_META[g]?.description ?? '';

export const getUserStatusLabel = (s: UserStatus) => USER_STATUS_META[s]?.label ?? String(s);
export const getUserStatusDescription = (s: UserStatus) => USER_STATUS_META[s]?.description ?? '';
